"use client";

/**
 * TranslateButton
 * Floating Eqqo-powered language selector for the public landing page.
 * The TranslatorProvider that powers translation lives in V2LandingPagePublicWrapper.
 */

import { LanguageSelector } from "eqqo-react";
import { EQQO_SUPPORTED_LANGUAGES } from "@/features/enterprise-sidebar/_logic/constants";

interface TranslateButtonProps {
  /** Optional sibling rendered beside the language selector */
  children?: React.ReactNode;
  /** Hides the selector (and children) below the md breakpoint, e.g. while the full-screen chat is open */
  isHiddenOnMobile?: boolean;
}

/**
 * Floating bottom-left language selector. `LanguageSelector` self-positions
 * via its `position` prop; optional children (e.g. the organizer "Edit Landing
 * Page" button) sit beside the selector at the same edge.
 *
 * The selector's fixed positioning is inline in the library, so it is hidden by
 * hiding this `contents` wrapper rather than by restyling the selector itself.
 *
 * @param props - Children to render beside the selector, and whether to hide everything on mobile
 * @returns The floating selector and its optional sibling
 */
export function TranslateButton({ children, isHiddenOnMobile = false }: TranslateButtonProps): React.ReactElement {
  return (
    <div className={isHiddenOnMobile ? "contents max-md:hidden" : "contents"}>
      <LanguageSelector languages={EQQO_SUPPORTED_LANGUAGES} variant="searchable" position="bottomLeft" />
      {children && (
        <div className="fixed bottom-5 left-20 z-50" data-no-translate="true">
          {children}
        </div>
      )}
    </div>
  );
}
